// ─────────────────────────────────────────────────────────────────────────────
// Tab7_TrainerPanel.jsx — Phase 2 (P2-S2)
// Exercise management panel — visible ONLY to TRAINER role.
// Rendered from Tab7_Exercise.jsx: {userRole === 'TRAINER' && <Tab7TrainerPanel />}
//
// Features:
//   - Create exercise room (loads current Tab 1–6 scenario as snapshot)
//   - Student roster: add, remove, auto-generate codes
//   - Import students from CSV
//   - Export roster as PDF (jsPDF)
//   - Open / Close room toggle
//   - Exercise selector (switch between trainer's exercises)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Alert, CircularProgress, Tooltip, Stack, Divider,
  Accordion, AccordionSummary, AccordionDetails, Slider,
} from '@mui/material';
import ExpandMoreIcon       from '@mui/icons-material/ExpandMore';
import AddIcon              from '@mui/icons-material/Add';
import DeleteOutlineIcon    from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon     from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon       from '@mui/icons-material/UploadFile';
import ContentCopyIcon      from '@mui/icons-material/ContentCopy';
import LockIcon             from '@mui/icons-material/Lock';
import LockOpenIcon         from '@mui/icons-material/LockOpen';
import GroupsIcon           from '@mui/icons-material/Groups';
import RefreshIcon          from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HistoryIcon          from '@mui/icons-material/History';
import FileDownloadIcon     from '@mui/icons-material/FileDownload';
import AssessmentIcon       from '@mui/icons-material/Assessment';
import PersonAddIcon        from '@mui/icons-material/PersonAdd';
import LinkIcon             from '@mui/icons-material/Link';
import FlashOnIcon          from '@mui/icons-material/FlashOn';
import QrCode2Icon          from '@mui/icons-material/QrCode2';
import { QRCodeCanvas }     from 'qrcode.react';
import jsPDF                from 'jspdf';
import autoTable            from 'jspdf-autotable';

import { exerciseService }      from '../services/exerciseService';
import { realtimeService }      from '../services/realtimeService'; // P2-S4
import { cloudStorageService }  from '../services/cloudStorageService'; // cloud backup
import { useUserRole }          from '../context/UserRoleContext';
import { useTranslation }       from 'react-i18next';

// ── Rank options ──────────────────────────────────────────────────────────────
const RANKS = [
  'PVT','CPL','SGT','SSG','SFC','MSG','SGM',      // Enlisted
  'WO1','WO2','WO3','WO4','WO5',                  // Warrant
  '2LT','1LT','CPT','MAJ','LTC','COL',            // Officers
  'BG','MG','LTG','GEN',                          // Generals
];

// ── Country codes (NATO + partners) ──────────────────────────────────────────
const COUNTRIES = [
  'ALB','BEL','BGR','CAN','HRV','CZE','DNK','EST','FIN','FRA',
  'DEU','GRC','HUN','ISL','ITA','LVA','LTU','LUX','MNE','NLD',
  'MKD','NOR','POL','PRT','ROU','SVK','SVN','ESP','TUR','GBR','USA',
];

// ── Status chip helper ────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const { t } = useTranslation();
  return (
    <Chip
      label={status === 'OPEN' ? `● ${t('tab7_trainer.status_open')}` : `○ ${t('tab7_trainer.status_closed')}`}
      color={status === 'OPEN' ? 'success' : 'default'}
      size="small"
      sx={{ fontSize: '0.7rem', fontWeight: 700 }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// P2-S4: onExerciseSelected(sessionCode|null) — called when trainer connects to/from a room
export default function Tab7TrainerPanel({ scenario, onExerciseSelected }) {
  // ── Role context ───────────────────────────────────────────────────────────
  const { isAdmin } = useUserRole();
  const { t } = useTranslation();

  // ── State ──────────────────────────────────────────────────────────────────
  const [exercises,       setExercises]       = useState([]);
  const [activeExercise,  setActiveExercise]  = useState(null); // full exercise + students
  const [loading,         setLoading]         = useState(true);
  const [actionLoading,   setActionLoading]   = useState(false);
  const [error,           setError]           = useState(null);
  const [successMsg,      setSuccessMsg]      = useState(null);
  const [codeCopied,      setCodeCopied]      = useState(false);

  // Session history (loaded from Supabase exercise_sessions)
  const [sessionHistory,  setSessionHistory]  = useState([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);

  // Create exercise dialog
  const [createOpen,      setCreateOpen]      = useState(false);
  const [newExName,       setNewExName]       = useState('');

  // Add student dialog
  const [addStudentOpen,  setAddStudentOpen]  = useState(false);
  const [newStudentName,  setNewStudentName]  = useState('');
  const [newStudentRank,  setNewStudentRank]  = useState('');
  const [newStudentCountry, setNewStudentCountry] = useState('');
  const [newStudentCode,  setNewStudentCode]  = useState('');

  // CSV import
  const csvInputRef = useRef(null);
  const [csvError,        setCsvError]        = useState(null);

  // P2-S5: Live Decision Board — keyed by student_id, updated via Realtime broadcast
  const [liveDecisions,   setLiveDecisions]   = useState({}); // { student_id: payload }

  // Co-organiser invite
  const [coOrganisers,    setCoOrganisers]    = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLink,      setInviteLink]      = useState('');
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [inviteLoading,   setInviteLoading]   = useState(false);

  // TRAINER-GATE-001: trainer invite generation (admin only)
  const [trainerInviteDialogOpen, setTrainerInviteDialogOpen] = useState(false);
  const [trainerInviteLink,       setTrainerInviteLink]       = useState('');
  const [trainerInviteCopied,     setTrainerInviteCopied]     = useState(false);
  const [trainerInviteLoading,    setTrainerInviteLoading]    = useState(false);
  const [trainerInvites,          setTrainerInvites]          = useState([]);

  // Admin export
  const [adminExportLoading, setAdminExportLoading] = useState(false);

  // Admin invite cleanup
  const [clearInvitesLoading, setClearInvitesLoading] = useState(false);

  // Course Dashboard — aggregate view across all exercises
  const [courseSummary,    setCourseSummary]    = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [showClosedEx,     setShowClosedEx]     = useState(false);

  // SF-2: QR join code dialog
  const [qrDialogOpen,          setQrDialogOpen]          = useState(false);

  // P3-IMP-001: Custom inject builder
  const [customInjectOpen,      setCustomInjectOpen]      = useState(false);
  const [customInjectTitle,     setCustomInjectTitle]     = useState('');
  const [customInjectNarrative, setCustomInjectNarrative] = useState('');
  const [customInjectOpDay,     setCustomInjectOpDay]     = useState(1);
  const [customInjectOptions,   setCustomInjectOptions]   = useState([
    { key: 'A', label: '', delta_dow: 0, delta_blood: 0 },
    { key: 'B', label: '', delta_dow: 0, delta_blood: 0 },
  ]);

  // ── Load co-organisers for the active exercise ───────────────────────────
  const loadCoOrganisers = useCallback(async (exerciseId) => {
    if (!exerciseId) { setCoOrganisers([]); return; }
    try {
      const list = await exerciseService.getCoOrganisers(exerciseId);
      setCoOrganisers(list || []);
    } catch (_) {
      setCoOrganisers([]);
    }
  }, []);

  // ── Course Dashboard — aggregate stats across all exercises ──────────────
  const loadCourseSummary = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const list = await exerciseService.getCourseSummary();
      setCourseSummary(list);
    } catch (_) {
      // Dashboard is informational — silent failure, stale data is acceptable
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  // TRAINER-GATE-001: load trainer invite history when admin mounts
  useEffect(() => {
    if (!isAdmin) return;
    cloudStorageService.getTrainerInvites()
      .then(list => setTrainerInvites(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, [isAdmin]);

  // ── P3-IMP-001: Fire a custom inject to all connected students ───────────
  const handleFireCustomInject = useCallback(() => {
    const validOptions = customInjectOptions.filter(o => o.label.trim());
    if (!customInjectTitle.trim() || validOptions.length < 2) return;
    realtimeService.send('custom_inject', {
      id:        crypto.randomUUID(),
      title:     customInjectTitle.trim(),
      narrative: customInjectNarrative.trim(),
      options:   validOptions,
      op_day:    customInjectOpDay,
      fired_at:  new Date().toISOString(),
    });
    setCustomInjectOpen(false);
    // Reset form for next use
    setCustomInjectTitle('');
    setCustomInjectNarrative('');
    setCustomInjectOpDay(1);
    setCustomInjectOptions([
      { key: 'A', label: '', delta_dow: 0, delta_blood: 0 },
      { key: 'B', label: '', delta_dow: 0, delta_blood: 0 },
    ]);
    setSuccessMsg('Custom inject fired to all connected students.');
    setTimeout(() => setSuccessMsg(null), 4000);
  }, [customInjectTitle, customInjectNarrative, customInjectOpDay, customInjectOptions]);

  // ── Load session history for the active exercise ─────────────────────────
  const loadSessionHistory = useCallback(async (exerciseId) => {
    if (!exerciseId) { setSessionHistory([]); return; }
    setHistoryLoading(true);
    try {
      const sessions = await exerciseService.getExerciseSessions(exerciseId);
      setSessionHistory(sessions);
    } catch (_) {
      setSessionHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // ── Select exercise (used by both dropdown and Course Dashboard Open btn) ─
  const handleSelectExercise = useCallback(async (exerciseId) => {
    try {
      const full = await exerciseService.getExerciseWithStudents(exerciseId);
      setActiveExercise(full);
      setLiveDecisions({});
      loadSessionHistory(full.id);
      loadCoOrganisers(full.id);
      onExerciseSelected?.(full.status === 'OPEN' ? full.session_code : null);
    } catch (_) {
      // Select failure leaves activeExercise unchanged
    }
  }, [loadSessionHistory, loadCoOrganisers, onExerciseSelected]);

  // ── Load exercises on mount ───────────────────────────────────────────────
  const loadExercises = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await exerciseService.getExercises();
      setExercises(list);
      // Auto-select the most recent OPEN exercise, or the most recent one
      if (list.length > 0 && !activeExercise) {
        const open = list.find(e => e.status === 'OPEN') || list[0];
        const full = await exerciseService.getExerciseWithStudents(open.id);
        setActiveExercise(full);
        loadSessionHistory(full.id);
        loadCoOrganisers(full.id);
        // P2-S4: connect trainer to broadcast channel for the selected exercise
        if (full.status === 'OPEN') onExerciseSelected?.(full.session_code);
      } else if (activeExercise) {
        // Refresh active exercise
        const full = await exerciseService.getExerciseWithStudents(activeExercise.id);
        setActiveExercise(full);
        loadSessionHistory(full.id);
        loadCoOrganisers(full.id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load exercises.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadExercises(); }, [loadExercises]);
  useEffect(() => { loadCourseSummary(); }, [loadCourseSummary]);

  // P2-S5: Subscribe to student decisions — handler registers on the singleton channel
  // (channel is connected by Tab7_Exercise when trainer selects an OPEN exercise)
  useEffect(() => {
    const off = realtimeService.on('student_decision', (payload) => {
      setLiveDecisions(prev => ({ ...prev, [payload.student_id]: payload }));
    });
    return off;
  }, []);

  // ── Show success message briefly ─────────────────────────────────────────
  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ── Create exercise ───────────────────────────────────────────────────────
  const handleCreateExercise = async () => {
    if (!newExName.trim()) return;
    setActionLoading(true);
    setError(null);
    try {
      const created = await exerciseService.createExercise({
        name:             newExName.trim(),
        scenarioSnapshot: scenario ?? null,
      });
      const full = await exerciseService.getExerciseWithStudents(created.id);
      setActiveExercise(full);
      onExerciseSelected?.(full.session_code); // P2-S4: connect to new exercise channel
      setExercises(prev => [created, ...prev]);
      setCreateOpen(false);
      setNewExName('');
      showSuccess(`Exercise "${created.name}" created — code: ${created.session_code}`);
      loadCourseSummary(); // refresh dashboard totals
    } catch (err) {
      setError(err.message || 'Failed to create exercise.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Toggle room open/closed ───────────────────────────────────────────────
  const handleToggleStatus = async () => {
    if (!activeExercise) return;
    const newStatus = activeExercise.status === 'OPEN' ? 'CLOSED' : 'OPEN';
    setActionLoading(true);
    try {
      const updated = await exerciseService.updateStatus(activeExercise.id, newStatus);
      setActiveExercise(prev => ({ ...prev, status: updated.status }));
      setExercises(prev => prev.map(e => e.id === updated.id ? { ...e, status: updated.status } : e));
      // P2-S4: notify connected students when room is closed
      if (newStatus === 'CLOSED') realtimeService.send('room_closed', {});
      onExerciseSelected?.(newStatus === 'OPEN' ? activeExercise.session_code : null);
      showSuccess(newStatus === 'CLOSED' ? 'Room closed — no new students can join.' : 'Room reopened.');
    } catch (err) {
      setError(err.message || 'Failed to update room status.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Regenerate student code ───────────────────────────────────────────────
  const regenerateCode = () => {
    const existing = (activeExercise?.students || []).map(s => s.personal_code);
    setNewStudentCode(exerciseService.generatePersonalCode(existing));
  };

  // Open add student dialog — pre-generate a code
  const openAddStudent = () => {
    regenerateCode();
    setNewStudentName('');
    setNewStudentRank('');
    setNewStudentCountry('');
    setAddStudentOpen(true);
  };

  // ── Add student ───────────────────────────────────────────────────────────
  const handleAddStudent = async () => {
    if (!newStudentName.trim() || !newStudentCode.trim()) return;
    setActionLoading(true);
    setError(null);
    try {
      const student = await exerciseService.addStudent(activeExercise.id, {
        name:         newStudentName.trim(),
        rank:         newStudentRank,
        country:      newStudentCountry,
        personalCode: newStudentCode.trim().toUpperCase(),
      });
      setActiveExercise(prev => ({
        ...prev,
        students: [...(prev.students || []), student].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setAddStudentOpen(false);
      showSuccess(`${student.name} added — code: ${student.personal_code}`);
    } catch (err) {
      setError(err.message?.includes('unique') ? 'That code is already used — regenerate.' : (err.message || 'Failed to add student.'));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Remove student ────────────────────────────────────────────────────────
  const handleRemoveStudent = async (student) => {
    if (!window.confirm(`Remove ${student.name} from the roster? This cannot be undone.`)) return;
    setActionLoading(true);
    try {
      await exerciseService.removeStudent(student.id);
      setActiveExercise(prev => ({
        ...prev,
        students: prev.students.filter(s => s.id !== student.id),
      }));
    } catch (err) {
      setError(err.message || 'Failed to remove student.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Copy session code to clipboard ────────────────────────────────────────
  const handleCopyCode = () => {
    navigator.clipboard.writeText(activeExercise?.session_code || '');
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  // ── Generate co-organiser invite link ─────────────────────────────────────
  const handleInviteCoOrganiser = async () => {
    if (!activeExercise) return;
    setInviteLoading(true);
    setError(null);
    try {
      const result = await exerciseService.createCoOrganiserInvite(activeExercise.id);
      const baseUrl = window.location.origin + window.location.pathname;
      const link = `${baseUrl}?invite=${result.token}`;
      setInviteLink(link);
      setInviteDialogOpen(true);
    } catch (err) {
      setError(err.message || 'Failed to generate invite link.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteLinkCopied(true);
    setTimeout(() => setInviteLinkCopied(false), 2500);
  };

  // TRAINER-GATE-001: Generate a trainer invite link (admin only)
  const handleCreateTrainerInvite = async () => {
    setTrainerInviteLoading(true);
    try {
      const result = await cloudStorageService.createTrainerInvite();
      const baseUrl = window.location.origin + window.location.pathname;
      setTrainerInviteLink(`${baseUrl}?trainer_invite=${result.token}`);
      setTrainerInviteDialogOpen(true);
      setTrainerInviteCopied(false);
      // Refresh invite history
      cloudStorageService.getTrainerInvites()
        .then(list => setTrainerInvites(Array.isArray(list) ? list : []))
        .catch(() => {});
    } catch (err) {
      setError(err.message || 'Failed to generate trainer invite link.');
    } finally {
      setTrainerInviteLoading(false);
    }
  };

  const handleCopyTrainerInviteLink = () => {
    navigator.clipboard.writeText(trainerInviteLink);
    setTrainerInviteCopied(true);
    setTimeout(() => setTrainerInviteCopied(false), 2500);
  };

  const handleClearExpiredInvites = async () => {
    const expiredCount = trainerInvites.filter(
      inv => !inv.used_at && new Date(inv.expires_at) < new Date()
    ).length;
    if (expiredCount === 0) return;
    if (!window.confirm(
      `Delete ${expiredCount} expired invite${expiredCount !== 1 ? 's' : ''}?\n\nThese tokens are already invalid and cannot be used. This cannot be undone.`
    )) return;
    setClearInvitesLoading(true);
    setError(null);
    try {
      const result = await cloudStorageService.clearExpiredTrainerInvites();
      cloudStorageService.getTrainerInvites()
        .then(list => setTrainerInvites(Array.isArray(list) ? list : []))
        .catch(() => {});
      showSuccess(`${result.deleted} expired invite${result.deleted !== 1 ? 's' : ''} deleted.`);
    } catch (err) {
      setError(err.message || 'Failed to clear expired invites.');
    } finally {
      setClearInvitesLoading(false);
    }
  };

  // ── Admin: export all scenarios ───────────────────────────────────────────
  const handleAdminExport = async () => {
    setAdminExportLoading(true);
    try {
      const allScenarios = await cloudStorageService.adminExportAll();
      const payload = {
        export_metadata: {
          exported_at:  new Date().toISOString(),
          count:        Array.isArray(allScenarios) ? allScenarios.length : 0,
          exported_by:  'ADMIN',
        },
        scenarios: allScenarios || [],
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `PSP_CRE_ALL_SCENARIOS_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Admin export failed:', err);
      setError('Export failed: ' + (err.message || 'Unknown error'));
      setTimeout(() => setError(null), 6000);
    } finally {
      setAdminExportLoading(false);
    }
  };

  // ── Import CSV ────────────────────────────────────────────────────────────
  const handleCsvChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvError(null);
    const text = await file.text();
    try {
      const parsed = exerciseService.parseRosterCsv(text);
      const existing = (activeExercise?.students || []).map(s => s.personal_code);
      setActionLoading(true);
      const added = [];
      for (const row of parsed) {
        if (!row.name) continue;
        const code = exerciseService.generatePersonalCode([...existing, ...added.map(s => s.personal_code)]);
        const student = await exerciseService.addStudent(activeExercise.id, {
          name: row.name, rank: row.rank, country: row.country, personalCode: code,
        });
        added.push(student);
      }
      setActiveExercise(prev => ({
        ...prev,
        students: [...(prev.students || []), ...added].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      showSuccess(`${added.length} student(s) imported from CSV.`);
    } catch (err) {
      setCsvError(err.message || 'CSV import failed. Check format: Name, Rank, Country columns.');
    } finally {
      setActionLoading(false);
      e.target.value = '';
    }
  };

  // ── P2-S6: Reopen a closed exercise from the archive ─────────────────────
  const handleReopenArchived = async (exerciseId) => {
    setActionLoading(true);
    setError(null);
    try {
      const updated = await exerciseService.updateStatus(exerciseId, 'OPEN');
      setExercises(prev => prev.map(e => e.id === exerciseId ? { ...e, status: 'OPEN' } : e));
      // Also switch active exercise to the reopened one
      const full = await exerciseService.getExerciseWithStudents(exerciseId);
      setActiveExercise(full);
      onExerciseSelected?.(full.session_code);
      showSuccess(`"${updated.name}" reopened.`);
    } catch (err) {
      setError(err.message || 'Failed to reopen exercise.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── P2-S6: Delete an exercise permanently ────────────────────────────────
  const handleDeleteExercise = async (ex) => {
    if (!window.confirm(
      `Permanently delete "${ex.name}"?\n\nAll students and session data will be removed. This cannot be undone.`
    )) return;
    setActionLoading(true);
    setError(null);
    try {
      await exerciseService.deleteExercise(ex.id);
      setExercises(prev => prev.filter(e => e.id !== ex.id));
      if (activeExercise?.id === ex.id) {
        setActiveExercise(null);
        onExerciseSelected?.(null);
      }
      showSuccess(`"${ex.name}" deleted.`);
    } catch (err) {
      setError(err.message || 'Failed to delete exercise.');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Export roster PDF ─────────────────────────────────────────────────────
  const handleExportPdf = () => {
    if (!activeExercise) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PSP/CRE — EXERCISE ROSTER', 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Exercise: ${activeExercise.name}`, 20, 32);
    doc.text(`Date: ${new Date(activeExercise.created_at).toLocaleDateString()}`, 20, 38);
    doc.text(`Session Code: ${activeExercise.session_code}`, 20, 44);
    doc.text('Classification: UNCLASSIFIED', 20, 50);

    // Instruction box
    doc.setDrawColor(0, 100, 200);
    doc.setLineWidth(0.3);
    doc.rect(20, 54, 170, 10);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text(
      'Open PSP/CRE app → enter Session Code above + YOUR CODE below → Join Exercise',
      105, 60.5, { align: 'center' }
    );

    // Roster table
    const rows = (activeExercise.students || []).map((s, i) => [
      i + 1,
      s.name,
      s.rank || '—',
      s.country || '—',
      s.personal_code,
    ]);

    autoTable(doc, {
      startY: 68,
      head:   [['#', 'Name', 'Rank', 'Country', 'YOUR CODE']],
      body:   rows,
      styles:      { fontSize: 10, cellPadding: 3 },
      headStyles:  { fillColor: [0, 70, 140], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        4: { fontStyle: 'bold', fontSize: 12 },
      },
      alternateRowStyles: { fillColor: [240, 245, 255] },
    });

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150);
      doc.text(
        `Generated by PSP/CRE · ${new Date().toLocaleString()} · UNCLASSIFIED`,
        105, 290, { align: 'center' }
      );
    }

    doc.save(`Roster_${activeExercise.session_code}_${activeExercise.name.replace(/\s+/g, '_')}.pdf`);
  };

  // ── Export all student sessions as JSON ───────────────────────────────────
  const handleExportJson = () => {
    if (!activeExercise) return;
    const payload = {
      export_metadata: {
        generated_at:   new Date().toISOString(),
        exercise_name:  activeExercise.name,
        session_code:   activeExercise.session_code,
        status:         activeExercise.status,
        created_at:     activeExercise.created_at,
        total_students: activeExercise.students?.length || 0,
        total_sessions: sessionHistory.length,
      },
      sessions: sessionHistory.map(s => ({
        student: {
          name:          s.students?.name          || 'Unknown',
          rank:          s.students?.rank          || null,
          country:       s.students?.country       || null,
          personal_code: s.students?.personal_code || null,
        },
        last_active: s.last_active,
        history:     s.history || [],
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Sessions_${activeExercise.session_code}_${activeExercise.name.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid', borderColor: 'primary.main' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <GroupsIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
            {t('tab7_trainer.accordion_header')}
          </Typography>
          <Chip label={t('tab7_trainer.accordion_trainer_chip')} color="primary" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
          {activeExercise && (
            <>
              <Box sx={{ flex: 1 }} />
              <StatusChip status={activeExercise.status} />
              <Chip
                label={`Code: ${activeExercise.session_code}`}
                variant="outlined" size="small"
                sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1 }}
              />
            </>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 2 }}>

        {/* ── Messages ───────────────────────────────────────────────────── */}
        {error      && <Alert severity="error"   sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 1.5 }}>{successMsg}</Alert>}
        {csvError   && <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setCsvError(null)}>{csvError}</Alert>}

        {/* ── Loading ────────────────────────────────────────────────────── */}
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">{t('tab7_trainer.loading_exercises')}</Typography>
          </Box>

        ) : exercises.length === 0 ? (
          /* ── No exercise yet ─────────────────────────────────────────── */
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('tab7_trainer.no_exercises_message')}
            </Typography>
            <Button
              variant="contained" startIcon={<AddCircleOutlineIcon />}
              onClick={() => setCreateOpen(true)}
            >
              {t('tab7_trainer.create_exercise_button')}
            </Button>
          </Box>

        ) : (
          /* ── Active exercise panel ───────────────────────────────────── */
          <Box>

            {/* ── Course Dashboard ─────────────────────────────────────── */}
            {(() => {
              const totals = courseSummary.reduce(
                (acc, ex) => ({
                  exercises:  acc.exercises  + 1,
                  students:   acc.students   + (ex.student_count   || 0),
                  sessions:   acc.sessions   + (ex.session_count   || 0),
                  decisions:  acc.decisions  + (ex.total_decisions || 0),
                }),
                { exercises: 0, students: 0, sessions: 0, decisions: 0 }
              );
              const closedCount = courseSummary.filter(ex => ex.status === 'CLOSED').length;
              const visibleExercises = showClosedEx
                ? courseSummary
                : courseSummary.filter(ex => ex.status !== 'CLOSED');

              const fmtLastActive = (ts) => {
                if (!ts) return '\u2014';
                const d   = new Date(ts);
                const now = new Date();
                const diffH = (now - d) / 3600000;
                if (diffH < 24) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
              };

              return (
                <Accordion defaultExpanded sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <AssessmentIcon color="action" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {t('tab7_trainer.course_overview_title')}
                      </Typography>
                      {dashboardLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
                      <Box sx={{ flex: 1 }} />
                      {closedCount > 0 && (
                        <Chip
                          label={showClosedEx
                            ? t('tab7_trainer.course_hide_closed')
                            : t('tab7_trainer.course_show_closed', { n: closedCount })}
                          size="small"
                          variant={showClosedEx ? 'filled' : 'outlined'}
                          color={showClosedEx ? 'default' : 'default'}
                          onClick={(e) => { e.stopPropagation(); setShowClosedEx(prev => !prev); }}
                          sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer', mr: 0.5 }}
                        />
                      )}
                      <Chip label={`${totals.exercises} exercise${totals.exercises !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ pt: 0 }}>
                    {/* Summary chips */}
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip icon={<GroupsIcon />}    label={`${totals.students} students`}   size="small" color="primary"  variant="outlined" />
                      <Chip icon={<HistoryIcon />}   label={`${totals.sessions} sessions`}   size="small" color="default"  variant="outlined" />
                      <Chip icon={<AssessmentIcon />} label={`${totals.decisions} decisions`} size="small" color="secondary" variant="outlined" />
                    </Stack>

                    <Divider sx={{ mb: 1 }} />

                    {/* Exercise table */}
                    {courseSummary.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">{t('tab7_trainer.course_overview_no_data')}</Typography>
                    ) : (
                      <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tab7_trainer.table_header_exercise')}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tab7_trainer.table_header_status')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tab7_trainer.table_header_students')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tab7_trainer.table_header_sessions')}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tab7_trainer.table_header_decisions')}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{t('tab7_trainer.table_header_last_active')}</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {visibleExercises.map(ex => (
                              <TableRow
                                key={ex.id}
                                hover
                                selected={activeExercise?.id === ex.id}
                                sx={{ '&.Mui-selected': { bgcolor: 'primary.50' } }}
                              >
                                <TableCell sx={{ fontSize: '0.78rem', maxWidth: 180 }}>
                                  <Tooltip title={`${ex.name} — ${ex.session_code}`}>
                                    <Box>
                                      <Typography variant="caption" fontWeight={600} noWrap display="block">
                                        {ex.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        {ex.session_code}
                                        {!ex.is_owner && (
                                          <Chip label={t('tab7_trainer.course_table_co_org_chip')} size="small" sx={{ ml: 0.5, fontSize: '0.6rem', height: 14 }} />
                                        )}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell><StatusChip status={ex.status} /></TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{ex.student_count}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{ex.session_count}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: ex.total_decisions > 0 ? 600 : 400 }}>
                                  {ex.total_decisions}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {fmtLastActive(ex.last_active)}
                                </TableCell>
                                <TableCell>
                                  {activeExercise?.id === ex.id ? (
                                    <Chip label={t('tab7_trainer.course_table_active_chip')} size="small" color="primary" sx={{ fontSize: '0.65rem', height: 20 }} />
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', py: 0, minWidth: 52 }}
                                      onClick={() => handleSelectExercise(ex.id)}
                                    >
                                      {t('tab7_trainer.course_table_open_button')}
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {!showClosedEx && closedCount > 0 && (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 0.5, textAlign: 'center', borderBottom: 'none' }}>
                                  <Typography
                                    variant="caption" color="text.disabled"
                                    sx={{ cursor: 'pointer', '&:hover': { color: 'text.secondary' } }}
                                    onClick={() => setShowClosedEx(true)}
                                  >
                                    {t('tab7_trainer.course_hidden_closed', { n: closedCount })}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })()}

            {/* Exercise selector + create button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>{t('tab7_trainer.active_exercise_label')}</InputLabel>
                <Select
                  label={t('tab7_trainer.active_exercise_label')}
                  value={activeExercise?.id || ''}
                  onChange={(e) => handleSelectExercise(e.target.value)}
                >
                  {exercises.map(ex => (
                    <MenuItem key={ex.id} value={ex.id}>
                      {ex.name} — {ex.session_code}
                      {ex.status === 'OPEN' ? ' ●' : ' ○'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />}
                onClick={() => setCreateOpen(true)}
              >
                {t('tab7_trainer.new_exercise_button')}
              </Button>
              <IconButton size="small" onClick={() => { loadExercises(); loadCourseSummary(); }} title={t('tab7_trainer.refresh_tooltip')}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {activeExercise && (
              <>
                {/* Exercise details row */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.5, mb: 2, bgcolor: 'grey.50', borderRadius: 1,
                  flexWrap: 'wrap', border: '1px solid', borderColor: 'divider',
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {activeExercise.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Created {new Date(activeExercise.created_at).toLocaleDateString()}
                      {' · '}{activeExercise.students?.length || 0} student(s)
                    </Typography>
                  </Box>

                  {/* Session code + copy + QR */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      label={`Session Code: ${activeExercise.session_code}`}
                      variant="outlined" color="primary"
                      sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1.5 }}
                    />
                    <Tooltip title={codeCopied ? t('tab7_trainer.copy_code_tooltip_copied') : t('tab7_trainer.copy_code_tooltip')}>
                      <IconButton size="small" onClick={handleCopyCode}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('tab7_trainer.qr_code_tooltip')}>
                      <IconButton size="small" color="primary" onClick={() => setQrDialogOpen(true)}>
                        <QrCode2Icon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Open/Close toggle */}
                  <Button
                    size="small"
                    variant={activeExercise.status === 'OPEN' ? 'outlined' : 'contained'}
                    color={activeExercise.status === 'OPEN' ? 'error' : 'success'}
                    startIcon={activeExercise.status === 'OPEN' ? <LockIcon /> : <LockOpenIcon />}
                    onClick={handleToggleStatus}
                    disabled={actionLoading}
                  >
                    {activeExercise.status === 'OPEN' ? t('tab7_trainer.exercise_status_open') : t('tab7_trainer.exercise_status_closed')}
                  </Button>
                </Box>

                {/* Roster toolbar */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 1 }}>
                    {t('tab7_trainer.student_roster_title')}
                  </Typography>
                  <Button
                    size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={openAddStudent}
                    disabled={actionLoading}
                  >
                    {t('tab7_trainer.add_student_button')}
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={<UploadFileIcon />}
                    onClick={() => csvInputRef.current?.click()}
                    disabled={actionLoading}
                  >
                    {t('tab7_trainer.import_csv_button')}
                  </Button>
                  <input
                    ref={csvInputRef} type="file" accept=".csv" hidden
                    onChange={handleCsvChange}
                  />
                  <Button
                    size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
                    onClick={handleExportPdf}
                    disabled={!activeExercise.students?.length}
                    color="secondary"
                  >
                    {t('tab7_trainer.export_pdf_button')}
                  </Button>
                  {/* P3-IMP-001: Custom inject */}
                  {activeExercise.status === 'OPEN' && (
                    <Button
                      size="small" variant="contained" color="warning"
                      startIcon={<FlashOnIcon />}
                      onClick={() => setCustomInjectOpen(true)}
                    >
                      {t('tab7_trainer.custom_inject_button')}
                    </Button>
                  )}
                </Box>

                {/* Roster table */}
                {!activeExercise.students?.length ? (
                  <Alert severity="info" icon={false} sx={{ fontSize: '0.8rem' }}>
                    {t('tab7_trainer.no_students_alert')}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: 40 }}>{t('tab7_trainer.roster_table_number_header')}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t('tab7_trainer.roster_table_name_header')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 70 }}>{t('tab7_trainer.roster_table_rank_header')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 70 }}>{t('tab7_trainer.roster_table_country_header')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 120 }}>{t('tab7_trainer.roster_table_code_header')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 100 }}>{t('tab7_trainer.roster_table_status_header')}</TableCell>
                          <TableCell sx={{ width: 40 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeExercise.students.map((s, i) => (
                          <TableRow key={s.id} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{s.name}</TableCell>
                            <TableCell>{s.rank || '—'}</TableCell>
                            <TableCell>{s.country || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={s.personal_code}
                                size="small" variant="outlined"
                                sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              {liveDecisions[s.id] ? (
                                <Chip label={`D+${liveDecisions[s.id].op_day} ✓`}
                                  size="small" color="success" variant="outlined"
                                  sx={{ fontSize: '0.65rem' }} />
                              ) : (
                                <Chip label={t('tab7_trainer.roster_status_waiting')} size="small" variant="outlined"
                                  sx={{ fontSize: '0.65rem', color: 'text.secondary' }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={t('tab7_trainer.roster_remove_tooltip')}>
                                <IconButton
                                  size="small" color="error"
                                  onClick={() => handleRemoveStudent(s)}
                                  disabled={actionLoading}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('tab7_trainer.students_received_decisions', { students: activeExercise.students?.length || 0, decisions: Object.keys(liveDecisions).length })}
                </Typography>

                {/* ── Co-organisers ─────────────────────────────────────────── */}
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('tab7_trainer.co_organisers_label')}
                  </Typography>
                  {coOrganisers.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">{t('tab7_trainer.co_organisers_none')}</Typography>
                  ) : (
                    coOrganisers.map(co => (
                      <Chip
                        key={co.trainer_id}
                        label={co.name || co.trainer_id.slice(0, 8)}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ))
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={handleInviteCoOrganiser}
                    disabled={inviteLoading || actionLoading}
                    sx={{ ml: 0.5, fontSize: '0.72rem', py: 0.25 }}
                  >
                    {inviteLoading ? t('tab7_trainer.invite_co_organiser_generating') : t('tab7_trainer.invite_co_organiser_button')}
                  </Button>
                </Box>

                {/* ── Session History ──────────────────────────────────────── */}
                <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <HistoryIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('tab7_trainer.session_history_title')}
                    </Typography>
                    {historyLoading && <CircularProgress size={14} />}
                    {!historyLoading && sessionHistory.length > 0 && (
                      <Chip
                        label={`${sessionHistory.length} student(s) recorded`}
                        size="small" variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      />
                    )}
                    <Box sx={{ flex: 1 }} />
                    {sessionHistory.length > 0 && (
                      <Button
                        size="small" variant="outlined" color="secondary"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportJson}
                      >
                        {t('tab7_trainer.export_json_button')}
                      </Button>
                    )}
                    <Tooltip title={t('tab7_trainer.session_history_refresh_tooltip')}>
                      <IconButton size="small" onClick={() => loadSessionHistory(activeExercise?.id)}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {!historyLoading && sessionHistory.length === 0 && (
                    <Alert severity="info" icon={false} sx={{ fontSize: '0.78rem' }}>
                      {t('tab7_trainer.session_history_no_data')}
                    </Alert>
                  )}

                  {sessionHistory.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{t('tab7_trainer.session_history_table_student')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 110 }}>{t('tab7_trainer.session_history_table_last_active')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 60, textAlign: 'center' }}>
                              {t('tab7_trainer.session_history_table_decisions')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 80 }}>{t('tab7_trainer.session_history_table_threat')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 110 }}>{t('tab7_trainer.session_history_table_action')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 70, textAlign: 'right' }}>{t('tab7_trainer.session_history_table_delta_dow')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 70, textAlign: 'right' }}>{t('tab7_trainer.session_history_table_delta_blood')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sessionHistory.map(s => {
                            const hist       = s.history || [];
                            const totalDow   = hist.reduce((sum, h) => sum + (h.delta?.total_dow   || 0), 0);
                            const totalBlood = hist.reduce((sum, h) => sum + (h.delta?.blood_units || 0), 0);
                            const lastEntry  = hist[hist.length - 1];
                            const threat     = lastEntry?.threat_class;
                            const action     = lastEntry?.rec_action;
                            const confidence = lastEntry?.confidence;
                            return (
                              <TableRow key={s.id} hover>
                                <TableCell sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                                  {s.students?.name || 'Unknown'}
                                  {s.students?.rank && (
                                    <Typography component="span" variant="caption"
                                      color="text.secondary" sx={{ ml: 0.5 }}>
                                      {s.students.rank}
                                    </Typography>
                                  )}
                                  {s.students?.country && (
                                    <Typography component="span" variant="caption"
                                      color="text.secondary" sx={{ ml: 0.5 }}>
                                      [{s.students.country}]
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                  {s.last_active
                                    ? new Date(s.last_active).toLocaleString(undefined, {
                                        month: 'short', day: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                      })
                                    : '—'}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  <Chip
                                    label={hist.length}
                                    size="small" variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 18, minWidth: 28 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {threat ? (
                                    <Chip
                                      label={threat} size="small"
                                      color={threat === 'LIFE' || threat === 'BOTH' ? 'error' : 'warning'}
                                      sx={{ fontSize: '0.6rem', height: 18 }}
                                    />
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    {action ? (
                                      <Chip
                                        label={action} size="small"
                                        color={action === 'EVACUATE' ? 'error' : action === 'PREPOSITION' ? 'warning' : 'default'}
                                        sx={{ fontSize: '0.6rem', height: 18 }}
                                      />
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">—</Typography>
                                    )}
                                    {confidence && (
                                      <Chip
                                        label={confidence} size="small" variant="outlined"
                                        color={confidence === 'HIGH' ? 'success' : confidence === 'MEDIUM' ? 'warning' : 'error'}
                                        sx={{ fontSize: '0.58rem', height: 16 }}
                                      />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{
                                  textAlign: 'right', fontWeight: 700, fontSize: '0.72rem',
                                  color: totalDow > 0 ? 'error.main' : totalDow < 0 ? 'success.main' : 'text.secondary',
                                }}>
                                  {totalDow !== 0 ? (totalDow > 0 ? `+${totalDow}` : totalDow) : '—'}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontSize: '0.68rem', color: 'text.secondary' }}>
                                  {totalBlood ? `${totalBlood}u` : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>

                {/* ── P2-S5: Live Decision Board ──────────────────────────── */}
                {Object.keys(liveDecisions).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      📡 {t('tab7_trainer.live_decision_board_title')}
                      <Chip label={t('tab7_trainer.live_decision_board_chip')} size="small" color="success" sx={{ fontSize: '0.6rem', height: 16, ml: 0.5 }} />
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{t('tab7_trainer.live_decision_table_student')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 50 }}>{t('tab7_trainer.live_decision_table_d_plus_n')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{t('tab7_trainer.live_decision_table_injects')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 70 }}>{t('tab7_trainer.live_decision_table_decision')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 80 }}>{t('tab7_trainer.live_decision_table_confidence')}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', width: 70 }}>{t('tab7_trainer.live_decision_table_delta_dow')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(activeExercise.students || []).map(s => {
                            const d = liveDecisions[s.id];
                            if (!d) return (
                              <TableRow key={s.id}>
                                <TableCell sx={{ fontSize: '0.72rem' }}>{s.name}</TableCell>
                                <TableCell colSpan={5}>
                                  <Typography variant="caption" color="text.disabled">{t('tab7_trainer.live_decision_waiting')}</Typography>
                                </TableCell>
                              </TableRow>
                            );
                            const deltaDow = d.delta?.total_dow ?? 0;
                            return (
                              <TableRow key={s.id} sx={{ bgcolor: 'success.50' }}>
                                <TableCell sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{s.name}</TableCell>
                                <TableCell sx={{ fontSize: '0.72rem' }}>D+{d.op_day}</TableCell>
                                <TableCell sx={{ fontSize: '0.68rem', maxWidth: 160 }}>
                                  {(d.decisions || []).map(dec => (
                                    <Chip key={dec.id} label={dec.label?.split('—')[0]?.trim() || dec.id}
                                      size="small" variant="outlined"
                                      sx={{ fontSize: '0.6rem', height: 18, mr: 0.25, mb: 0.25 }} />
                                  ))}
                                </TableCell>
                                <TableCell>
                                  {(d.decisions || []).map(dec => (
                                    <Chip key={dec.id} label={dec.decision || '?'}
                                      size="small" color="primary"
                                      sx={{ fontSize: '0.68rem', height: 20, mr: 0.25, fontWeight: 700 }} />
                                  ))}
                                </TableCell>
                                <TableCell>
                                  {(d.decisions || []).map(dec => dec.confidence && (
                                    <Chip key={dec.id}
                                      label={dec.confidence}
                                      size="small"
                                      color={dec.confidence === 'HIGH' ? 'success' : dec.confidence === 'MEDIUM' ? 'warning' : 'error'}
                                      sx={{ fontSize: '0.6rem', height: 18, mr: 0.25 }} />
                                  ))}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700,
                                  color: deltaDow > 0 ? 'error.main' : deltaDow < 0 ? 'success.main' : 'text.secondary' }}>
                                  {deltaDow > 0 ? `+${deltaDow}` : deltaDow}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Students who joined but are not on the roster */}
                          {Object.entries(liveDecisions)
                            .filter(([sid]) => !(activeExercise.students || []).find(s => s.id === sid))
                            .map(([sid, d]) => {
                              const deltaDow = d.delta?.total_dow ?? 0;
                              return (
                                <TableRow key={sid}>
                                  <TableCell sx={{ fontSize: '0.72rem', fontStyle: 'italic' }}>{d.student_name}</TableCell>
                                  <TableCell sx={{ fontSize: '0.72rem' }}>D+{d.op_day}</TableCell>
                                  <TableCell colSpan={2}>
                                    {(d.decisions || []).map(dec => (
                                      <Chip key={dec.id} label={dec.decision || '?'} size="small" color="primary"
                                        sx={{ fontSize: '0.68rem', height: 20, mr: 0.25, fontWeight: 700 }} />
                                    ))}
                                  </TableCell>
                                  <TableCell />
                                  <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700,
                                    color: deltaDow > 0 ? 'error.main' : deltaDow < 0 ? 'success.main' : 'text.secondary' }}>
                                    {deltaDow > 0 ? `+${deltaDow}` : deltaDow}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* ── P2-S6: Exercise Archive — CLOSED exercises ────────────────── */}
        {!loading && exercises.some(e => e.status === 'CLOSED') && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              📁 {t('tab7_trainer.exercise_archive_title')}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({exercises.filter(e => e.status === 'CLOSED').length} closed)
              </Typography>
            </Typography>
            {exercises.filter(e => e.status === 'CLOSED').map(ex => (
              <Box key={ex.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                mb: 0.75, flexWrap: 'wrap',
                p: 1, borderRadius: 1,
                bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
              }}>
                <Chip label={t('tab7_trainer.exercise_archive_closed_chip')} size="small" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 18, color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{ex.name}</Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ letterSpacing: 0.5, fontWeight: 600 }}>
                  {ex.session_code}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {new Date(ex.created_at).toLocaleDateString()}
                </Typography>
                <Button
                  size="small" variant="outlined" color="success"
                  onClick={() => handleReopenArchived(ex.id)}
                  disabled={actionLoading}
                  sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                >
                  {t('tab7_trainer.archive_reopen_button')}
                </Button>
                <Tooltip title={t('tab7_trainer.archive_delete_tooltip')}>
                  <IconButton
                    size="small" color="error"
                    onClick={() => handleDeleteExercise(ex)}
                    disabled={actionLoading}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Admin: Export all trainers' scenarios ─────────────────────── */}
        {isAdmin && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'warning.main' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'warning.dark' }}>
              🔐 {t('tab7_trainer.admin_export_title')}
            </Typography>
            <Alert severity="warning" icon={false} sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }}>
              {t('tab7_trainer.admin_export_alert')}
            </Alert>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={adminExportLoading
                ? <CircularProgress size={14} color="inherit" />
                : <FileDownloadIcon />
              }
              disabled={adminExportLoading}
              onClick={handleAdminExport}
            >
              {adminExportLoading ? t('tab7_trainer.admin_export_loading') : t('tab7_trainer.admin_export_button')}
            </Button>
          </Box>
        )}

        {/* ── TRAINER-GATE-001: Admin — Trainer Invite Links ────────────── */}
        {isAdmin && (
          <Box sx={{
            mt: 2.5, pt: 2,
            borderTop: '2px dashed',
            borderColor: 'warning.main',
          }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'warning.dark' }}>
              🔐 {t('tab7_trainer.trainer_invite_title')}
            </Typography>
            <Alert severity="warning" icon={false} sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }}>
              {t('tab7_trainer.trainer_invite_alert')}
            </Alert>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={trainerInviteLoading
                ? <CircularProgress size={14} color="inherit" />
                : <PersonAddIcon />
              }
              disabled={trainerInviteLoading}
              onClick={handleCreateTrainerInvite}
              sx={{ mb: trainerInvites.length > 0 ? 2 : 0 }}
            >
              {trainerInviteLoading ? t('tab7_trainer.trainer_invite_loading') : t('tab7_trainer.trainer_invite_button')}
            </Button>

            {/* Invite history table */}
            {trainerInvites.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, flex: 1 }}>
                    {t('tab7_trainer.trainer_invite_history_label')}
                  </Typography>
                  {trainerInvites.some(inv => !inv.used_at && new Date(inv.expires_at) < new Date()) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={clearInvitesLoading
                        ? <CircularProgress size={12} color="inherit" />
                        : <DeleteOutlineIcon fontSize="small" />
                      }
                      disabled={clearInvitesLoading}
                      onClick={handleClearExpiredInvites}
                      sx={{ fontSize: '0.68rem', py: 0.25, px: 1, minWidth: 0 }}
                    >
                      {t('tab7_trainer.clear_expired_invites_button')}
                    </Button>
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{t('tab7_trainer.trainer_invite_table_note')}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{t('tab7_trainer.trainer_invite_table_created')}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{t('tab7_trainer.trainer_invite_table_expires')}</TableCell>
                      <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>{t('tab7_trainer.trainer_invite_table_status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trainerInvites.map(inv => {
                      const now = new Date();
                      const expires = new Date(inv.expires_at);
                      const isUsed = !!inv.used_at;
                      const isExpired = !isUsed && expires < now;
                      const status = isUsed ? t('tab7_trainer.trainer_invite_status_used') : isExpired ? t('tab7_trainer.trainer_invite_status_expired') : t('tab7_trainer.trainer_invite_status_pending');
                      const statusColor = isUsed ? 'success' : isExpired ? 'default' : 'warning';
                      return (
                        <TableRow key={inv.id}>
                          <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>
                            {inv.note || <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>
                            {new Date(inv.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', py: 0.5 }}>
                            {expires.toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip label={status} size="small" color={statusColor}
                              sx={{ fontSize: '0.65rem', height: 18 }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>

      {/* ── P3-IMP-001: Custom Inject Dialog ─────────────────────────────── */}
      <Dialog open={customInjectOpen} onClose={() => setCustomInjectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlashOnIcon color="warning" />
          {t('tab7_trainer.custom_inject_dialog_title')}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: '0.78rem' }}>
            {t('tab7_trainer.custom_inject_alert')}
          </Alert>

          {/* Title */}
          <TextField
            label={t('tab7_trainer.custom_inject_title_label')} fullWidth size="small" sx={{ mb: 1.5 }}
            value={customInjectTitle}
            onChange={e => setCustomInjectTitle(e.target.value)}
            placeholder={t('tab7_trainer.custom_inject_title_placeholder')}
            required autoFocus
          />

          {/* Narrative */}
          <TextField
            label={t('tab7_trainer.custom_inject_narrative_label')} fullWidth size="small" multiline rows={3} sx={{ mb: 1.5 }}
            value={customInjectNarrative}
            onChange={e => setCustomInjectNarrative(e.target.value)}
            placeholder="At 14:35 local, ROLE 2B displaces 12 km due to indirect fire threat.
Surgical capacity reduced for an estimated 6 hours. Blood chain continuity unknown.
What is the medical commander's priority?"
          />

          {/* Op Day */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {t('tab7_trainer.custom_inject_op_day_label', { day: customInjectOpDay })}
            </Typography>
            <Slider
              value={customInjectOpDay}
              onChange={(_, v) => setCustomInjectOpDay(v)}
              min={1} max={30} step={1} marks
              valueLabelDisplay="auto"
              valueLabelFormat={v => `D+${v}`}
              sx={{ maxWidth: 300 }}
            />
          </Box>

          {/* Options */}
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
            {t('tab7_trainer.custom_inject_options_label', { count: customInjectOptions.length })}
          </Typography>

          {customInjectOptions.map((opt, idx) => (
            <Box key={opt.key} sx={{
              display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start',
              p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
            }}>
              <Chip label={opt.key} size="small" color="primary" variant="outlined"
                sx={{ mt: 0.5, minWidth: 28, fontWeight: 700 }} />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label={t('tab7_trainer.custom_inject_option_label')} size="small" fullWidth
                  value={opt.label}
                  onChange={e => setCustomInjectOptions(prev =>
                    prev.map((o, i) => i === idx ? { ...o, label: e.target.value } : o)
                  )}
                  placeholder={`Option ${opt.key}: e.g. Request blood resupply`}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label={t('tab7_trainer.custom_inject_delta_dow_label')} size="small" type="number"
                    sx={{ width: 100 }}
                    value={opt.delta_dow}
                    onChange={e => setCustomInjectOptions(prev =>
                      prev.map((o, i) => i === idx ? { ...o, delta_dow: Number(e.target.value) } : o)
                    )}
                    inputProps={{ step: 1 }}
                    helperText={t('tab7_trainer.custom_inject_delta_dow_helper')}
                  />
                  <TextField
                    label={t('tab7_trainer.custom_inject_delta_blood_label')} size="small" type="number"
                    sx={{ width: 110 }}
                    value={opt.delta_blood}
                    onChange={e => setCustomInjectOptions(prev =>
                      prev.map((o, i) => i === idx ? { ...o, delta_blood: Number(e.target.value) } : o)
                    )}
                    inputProps={{ step: 1 }}
                    helperText={t('tab7_trainer.custom_inject_delta_blood_helper')}
                  />
                </Box>
              </Box>
              {customInjectOptions.length > 2 && (
                <IconButton size="small" color="error" sx={{ mt: 0.5 }}
                  onClick={() => setCustomInjectOptions(prev => prev.filter((_, i) => i !== idx))}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}

          {customInjectOptions.length < 4 && (
            <Button
              size="small" variant="text" startIcon={<AddCircleOutlineIcon />}
              onClick={() => {
                const keys = ['A', 'B', 'C', 'D'];
                const nextKey = keys[customInjectOptions.length];
                setCustomInjectOptions(prev => [
                  ...prev,
                  { key: nextKey, label: '', delta_dow: 0, delta_blood: 0 },
                ]);
              }}
            >
              {t('tab7_trainer.custom_inject_add_option_button')}
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomInjectOpen(false)}>{t('tab7_trainer.custom_inject_cancel_button')}</Button>
          <Button
            variant="contained" color="warning"
            startIcon={<FlashOnIcon />}
            onClick={handleFireCustomInject}
            disabled={
              !customInjectTitle.trim() ||
              customInjectOptions.filter(o => o.label.trim()).length < 2
            }
          >
            {t('tab7_trainer.custom_inject_fire_button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Exercise Dialog ────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('tab7_trainer.create_exercise_dialog_title')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('tab7_trainer.create_exercise_name_label')} fullWidth size="small" sx={{ mt: 1 }}
            value={newExName} onChange={e => setNewExName(e.target.value)}
            placeholder={t('tab7_trainer.create_exercise_name_placeholder')}
            autoFocus
            helperText={t('tab7_trainer.create_exercise_name_helper')}
            onKeyDown={e => e.key === 'Enter' && handleCreateExercise()}
          />
          {scenario && (
            <Alert severity="success" icon={false} sx={{ mt: 1.5, fontSize: '0.78rem' }}>
              {t('tab7_trainer.create_exercise_scenario_success')}
            </Alert>
          )}
          {!scenario && (
            <Alert severity="warning" icon={false} sx={{ mt: 1.5, fontSize: '0.78rem' }}>
              {t('tab7_trainer.create_exercise_no_scenario')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('tab7_trainer.create_exercise_cancel_button')}</Button>
          <Button
            variant="contained" onClick={handleCreateExercise}
            disabled={actionLoading || !newExName.trim()}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('tab7_trainer.create_exercise_submit_button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Student Dialog ────────────────────────────────────────────── */}
      <Dialog open={addStudentOpen} onClose={() => setAddStudentOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('tab7_trainer.add_student_dialog_title')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('tab7_trainer.add_student_name_label')} fullWidth size="small" sx={{ mt: 1, mb: 1.5 }}
            value={newStudentName} onChange={e => setNewStudentName(e.target.value)}
            placeholder={t('tab7_trainer.add_student_name_placeholder')} autoFocus required
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>{t('tab7_trainer.add_student_rank_label')}</InputLabel>
              <Select label={t('tab7_trainer.add_student_rank_label')} value={newStudentRank} onChange={e => setNewStudentRank(e.target.value)}>
                <MenuItem value=""><em>—</em></MenuItem>
                {RANKS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>{t('tab7_trainer.add_student_country_label')}</InputLabel>
              <Select label={t('tab7_trainer.add_student_country_label')} value={newStudentCountry} onChange={e => setNewStudentCountry(e.target.value)}>
                <MenuItem value=""><em>—</em></MenuItem>
                {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label={t('tab7_trainer.add_student_code_label')} size="small" sx={{ flex: 1 }}
              value={newStudentCode} onChange={e => setNewStudentCode(e.target.value.toUpperCase())}
              inputProps={{ style: { fontWeight: 700, letterSpacing: 1.5 } }}
              helperText={t('tab7_trainer.add_student_code_helper')}
            />
            <Button
              size="small" variant="outlined" onClick={regenerateCode}
              sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
            >
              {t('tab7_trainer.add_student_regenerate_button')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddStudentOpen(false)}>{t('tab7_trainer.add_student_cancel_button')}</Button>
          <Button
            variant="contained" onClick={handleAddStudent}
            disabled={actionLoading || !newStudentName.trim() || !newStudentCode.trim()}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('tab7_trainer.add_student_submit_button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Co-organiser Invite Link Dialog ──────────────────────────────────── */}
      <Dialog open={inviteDialogOpen} onClose={() => { setInviteDialogOpen(false); setInviteLinkCopied(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="primary" />
          {t('tab7_trainer.co_organiser_dialog_title')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Send this link to another trainer. When they open it and log in (or register),
              they will be added as a co-organiser of <strong>{activeExercise?.name}</strong>.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('tab7_trainer.co_organiser_dialog_validity')}
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={inviteLink}
              InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              label={t('tab7_trainer.co_organiser_link_label')}
            />
            <Tooltip title={inviteLinkCopied ? t('tab7_trainer.co_organiser_copied') : t('tab7_trainer.co_organiser_copy_button')}>
              <Button
                variant={inviteLinkCopied ? 'contained' : 'outlined'}
                color={inviteLinkCopied ? 'success' : 'primary'}
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyInviteLink}
                sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
              >
                {inviteLinkCopied ? t('tab7_trainer.co_organiser_copied') : t('tab7_trainer.co_organiser_copy_button')}
              </Button>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Share via email, Teams, Signal, or any secure channel. Do not post publicly.
            The link grants full co-organiser access to this exercise.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInviteDialogOpen(false); setInviteLinkCopied(false); }}>
            {t('tab7_trainer.co_organiser_close_button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── TRAINER-GATE-001: Trainer Invite Link Dialog ─────────────────── */}
      <Dialog
        open={trainerInviteDialogOpen}
        onClose={() => { setTrainerInviteDialogOpen(false); setTrainerInviteCopied(false); }}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="warning" />
          {t('tab7_trainer.trainer_invite_dialog_title')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('tab7_trainer.trainer_invite_dialog_validity')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Recipients with an existing account will be upgraded to trainer status on login.
              <strong> Do not post this link publicly.</strong>
            </Typography>
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={trainerInviteLink}
              InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
              label={t('tab7_trainer.trainer_invite_link_label')}
            />
            <Tooltip title={trainerInviteCopied ? t('tab7_trainer.trainer_invite_copied') : t('tab7_trainer.trainer_invite_copy_button')}>
              <Button
                variant={trainerInviteCopied ? 'contained' : 'outlined'}
                color={trainerInviteCopied ? 'success' : 'warning'}
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyTrainerInviteLink}
                sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
              >
                {trainerInviteCopied ? t('tab7_trainer.trainer_invite_copied') : t('tab7_trainer.trainer_invite_copy_button')}
              </Button>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            The recipient will be prompted to create an account or log in. Once they complete that step,
            their account is automatically authorised as a trainer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTrainerInviteDialogOpen(false); setTrainerInviteCopied(false); }}>
            {t('tab7_trainer.trainer_invite_close_button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SF-2: QR Join Code Dialog ─────────────────────────────────────── */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2Icon color="primary" />
          {t('tab7_trainer.qr_dialog_title')}
        </DialogTitle>
        <DialogContent dividers>
          {activeExercise && (() => {
            const joinUrl = `${window.location.origin}${window.location.pathname}?session=${activeExercise.session_code}`;
            const canvasId = 'psp-qr-canvas';
            const handleDownload = () => {
              const canvas = document.getElementById(canvasId);
              if (!canvas) return;
              const link = document.createElement('a');
              link.download = `QR_${activeExercise.session_code}.png`;
              link.href = canvas.toDataURL('image/png');
              link.click();
            };
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
                {/* QR code */}
                <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <QRCodeCanvas
                    id={canvasId}
                    value={joinUrl}
                    size={220}
                    level="M"
                    includeMargin={false}
                  />
                </Box>

                {/* Session code large display */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('tab7_trainer.qr_session_code_label')}
                  </Typography>
                  <Typography
                    variant="h5" fontWeight={700} letterSpacing={3}
                    sx={{ fontFamily: 'monospace', color: 'primary.main' }}
                  >
                    {activeExercise.session_code}
                  </Typography>
                </Box>

                {/* URL display */}
                <Typography
                  variant="caption" color="text.secondary"
                  sx={{ wordBreak: 'break-all', textAlign: 'center', px: 1 }}
                >
                  {joinUrl}
                </Typography>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button
                    fullWidth variant="outlined" startIcon={<ContentCopyIcon />}
                    onClick={() => navigator.clipboard.writeText(joinUrl).catch(() => {})}
                  >
                    {t('tab7_trainer.qr_copy_link_button')}
                  </Button>
                  <Button
                    fullWidth variant="contained" startIcon={<QrCode2Icon />}
                    onClick={handleDownload}
                  >
                    {t('tab7_trainer.qr_download_button')}
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {t('tab7_trainer.qr_helper')}
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>{t('tab7_trainer.qr_close_button')}</Button>
        </DialogActions>
      </Dialog>

    </Accordion>
  );
}
